/* eslint-disable react/jsx-sort-props */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, IconButton, Input, Typography } from '@mui/joy';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Device } from '../models/Device';
import CampaignSchema from '../schema/campaignSchema';
import { CampaignService } from '../services/CampaignService';
import { CompanyService } from '../services/CompanyService';
import { DeviceService } from '../services/DeviceService';
import { OrganizationUser } from '../services/types/api.generated';
import campaignStore from '../stores/CampaignStore';
import organizationStore from '../stores/OrganizationStore';
import sessionStore from '../stores/SessionStore';
import CampaignCard from './Campaign/CampaignCard';
import { ActionRadioField } from './FormElements/ActionRadioField';
import { SelectField } from './FormElements/SelectField';
import { TextAreaField } from './FormElements/TextAreaField';
import { TextInputField } from './FormElements/TextInputField';
import GeneralModal from './GeneralModal';
import TableHeader from './TableHeader';

interface CampaignFormInput {
  name: string;
  description: string;
  device: Device;
  status: number;
  enable_vantis_send: boolean;
  dronesense_enabled?: boolean;
  dronesense_pilot?: number | null;
}

const CampaignList = observer(() => {
  const { campaigns, loading, error } = campaignStore;
  const { selectedOrg } = organizationStore;
  const { user } = sessionStore;
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6; // Number of campaigns per page
  const campaignOptions = [
    {
      label: t('campaigns.status_options.active'),
      value: 1,
      description: t('campaigns.status_options.active_description')
    },
    {
      label: t('campaigns.status_options.inactive'),
      value: 0,
      description: t('campaigns.status_options.inactive_description')
    }
  ];
  const activeCampaign = campaigns.find((campaign) => campaign.status === 1);
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    trigger
  } = useForm<CampaignFormInput>({
    resolver: yupResolver(CampaignSchema(t)),
    defaultValues: {
      name: '',
      description: '',
      device: undefined,
      status: 1,
      enable_vantis_send: false,
      dronesense_enabled: false,
      dronesense_pilot: null
    }
  });

  const dronesenseEnabled = watch('dronesense_enabled');

  useEffect(() => {
    if (!selectedOrg) {
      return;
    }
    campaignStore.fetchCampaigns(selectedOrg, t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchDevices = async () => {
      const res = await DeviceService.getAllDevices();
      if (res.success) {
        setDevices(res.data);
      } else {
        setDevices([]);
      }
    };
    fetchDevices();
  }, [selectedOrg]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!selectedOrg) {
          return;
        }
        const organization = await CompanyService.getOrganizationDetails(selectedOrg);
        setUsers(organization.data.users ?? []);
      } catch (e) {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    reset();
    setIsModalOpen(false);
  };

  const handleAddCampaign = async (data: CampaignFormInput): Promise<void> => {
    if (selectedOrg === null) {
      toast.error(t('general_errors.no_organization_selected'));
      return;
    }

    try {
      setSendingRequest(true);
      const response = await CampaignService.createCampaign({
        ...data,
        // Backend expects list of device_uuid strings for devices
        devices: [data.device.device_uuid],
        organization: selectedOrg,
        status: 1
      });
      if (!response) {
        toast.error(t('campaigns.errors.failed_to_create_campaign'));
        return;
      }
      campaignStore.updateCampaign(response);
      toast.success(t('campaigns.messages.campaign_created'));
      reset();
      setIsModalOpen(false);
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error(t('campaigns.errors.failed_to_create_campaign'));
      }
    } finally {
      setSendingRequest(false);
    }
  };

  const joinCampaign = async (campaignId: string): Promise<void> => {
    const foundCampaign = campaigns.find((campaign) => {
      if (campaign.id === campaignId) {
        return campaign;
      }
      return null;
    });
    if (!foundCampaign) {
      console.error('Campaign not found'); // this shouldn't happen.
      return;
    }
    navigate(`/campaign/${campaignId}`);
  };

  // Sort campaigns: active first, then by newest to oldest
  const sortedCampaigns = campaigns.slice().sort((a, b) => {
    // Active campaigns first
    if (a.status === 1 && b.status === 0) return -1;
    if (a.status === 0 && b.status === 1) return 1;

    // Then sort by start_time (newest first)
    const dateA = a.start_time ? new Date(a.start_time).getTime() : 0;
    const dateB = b.start_time ? new Date(b.start_time).getTime() : 0;
    return dateB - dateA;
  });

  const filteredCampaigns = sortedCampaigns.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
  const paginatedCampaigns = filteredCampaigns.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPage(value);
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  return (
    <>
      <TableHeader title='Campaigns'>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search'
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          <Button color='neutral' onClick={handleOpenModal}>
            {t('campaigns.add_campaign')}
          </Button>
        </Box>
      </TableHeader>

      <Box sx={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        {loading && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <CircularProgress />
            <Typography level='body-md' marginTop='15px'>
              {t('loading')}
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: 'center', padding: '50px' }}>
            <Typography color='danger' level='body-md'>
              {t('campaigns.loading_failed')}
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: '20px',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(1, 1fr)',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(3, 1fr)' // Changed from 4 to 3 columns
                },
                padding: '20px'
              }}
            >
              {paginatedCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  user={user}
                  campaign={campaign}
                  joinCampaign={joinCampaign}
                  activeCampaign={activeCampaign}
                />
              ))}
            </Box>

            {filteredCampaigns.length > itemsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
                <IconButton
                  disabled={page === 1}
                  onClick={() => handlePageChange(null, page - 1)}
                  variant='outlined'
                  color='neutral'
                  size='sm'
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                  <Typography level='body-md'>
                    {`${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, filteredCampaigns.length)} of ${
                      filteredCampaigns.length
                    }`}
                  </Typography>
                </Box>

                <IconButton
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(null, page + 1)}
                  variant='outlined'
                  color='neutral'
                  size='sm'
                >
                  <KeyboardArrowRightIcon />
                </IconButton>
              </Box>
            )}

            {filteredCampaigns.length === 0 && (
              <Box sx={{ textAlign: 'center', padding: '50px' }}>
                <Typography level='body-md'>{t('campaigns.no_campaigns_found')}</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <GeneralModal
        actions={
          <>
            <Button color='neutral' onClick={handleCloseModal} variant='plain'>
              {t('general_button_labels.cancel')}
            </Button>
            <Button color='neutral' disabled={sendingRequest} form='campaign-form' type='submit' variant='solid'>
              {t('general_button_labels.save')}
            </Button>
          </>
        }
        content={
          <form id='campaign-form' onSubmit={handleSubmit(handleAddCampaign)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: 550 }}>
              <Box>
                <Controller
                  control={control}
                  name='name'
                  render={({ field }) => (
                    <TextInputField
                      label={t('campaigns.name_label')}
                      placeholder={t('campaigns.name_placeholder')}
                      {...field}
                      error={errors.name?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='device'
                  render={({ field }) => (
                    <SelectField
                      label={t('campaigns.drone_label')}
                      placeholder={t('campaigns.drone_placeholder')}
                      {...field}
                      error={errors.device?.message}
                      options={devices.map((dev) => ({
                        label: `${dev.name ?? ''} - ${dev.serial_number ?? dev.device_uuid}`,
                        value: dev.device_uuid
                      }))}
                      value={field.value ? field.value.device_uuid : ''}
                      onChange={(selectedDeviceUuid) => {
                        const selectedDevice = devices.find((dev) => dev.device_uuid === selectedDeviceUuid);
                        field.onChange(selectedDevice);
                      }}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='description'
                  render={({ field }) => (
                    <TextAreaField
                      label={t('campaigns.campaign_outline_label')}
                      placeholder={t('campaigns.description_placeholder')}
                      minRows={4}
                      {...field}
                      error={errors.description?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='status'
                  render={({ field }) => (
                    <ActionRadioField
                      label={t('campaigns.status_label')}
                      {...field}
                      error={errors.status?.message}
                      options={campaignOptions.map((option) => ({
                        value: option.value.toString(),
                        label: option.label,
                        description: option.description
                      }))}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='enable_vantis_send'
                  render={({ field }) => (
                    <Checkbox
                      component='span'
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      label={t('campaigns.enable_vantis')}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='dronesense_enabled'
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value || false}
                      onChange={async (e) => {
                        field.onChange(e.target.checked);
                        await trigger('dronesense_pilot');
                      }}
                      label={t('campaigns.dronesense_enabled')}
                      sx={{ mt: 1 }}
                    />
                  )}
                />
              </Box>
              {dronesenseEnabled && (
                <Box>
                  <Controller
                    control={control}
                    name='dronesense_pilot'
                    render={({ field }) => (
                      <SelectField
                        label={t('campaigns.pilot_label')}
                        name='dronesense_pilot'
                        placeholder={loadingUsers ? t('campaigns.loading_users') : t('campaigns.pilot_placeholder')}
                        options={users.map((u) => ({
                          value: u?.id ?? '',
                          label: `${u.first_name ?? ''} ${u.last_name ?? ''} - ${u.email}`
                        }))}
                        value={field.value || ''}
                        onChange={async (value) => {
                          field.onChange(value);
                          await trigger('dronesense_pilot');
                        }}
                        error={errors.dronesense_pilot?.message}
                      />
                    )}
                  />
                </Box>
              )}
            </Box>
          </form>
        }
        onClose={handleCloseModal}
        open={isModalOpen}
        title={t('campaigns.add_title')}
      />
    </>
  );
});

export default CampaignList;
